"use client";

import { Check, Edit2, ExternalLink, X } from "lucide-react";
import { useState } from "react";

interface ContextTableProps {
  data: Record<string, unknown>;
  onEdit?: (key: string, value: unknown) => void;
}

export function ContextTable({ data, onEdit }: ContextTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-gray-300 bg-gray-100">
            <th className="text-left px-3 py-2 font-semibold text-gray-700">
              Key
            </th>
            <th className="text-left px-3 py-2 font-semibold text-gray-700">
              Type
            </th>
            <th className="text-left px-3 py-2 font-semibold text-gray-700">
              Value
            </th>
            {onEdit && (
              <th className="text-left px-3 py-2 font-semibold text-gray-700 w-24">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {Object.entries(data).map(([key, value]) => (
            <ContextTableRow
              key={key}
              keyName={key}
              value={value}
              onEdit={onEdit}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface ContextTableRowProps {
  keyName: string;
  value: unknown;
  onEdit?: (key: string, value: unknown) => void;
}

function ContextTableRow({ keyName, value, onEdit }: ContextTableRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [showModal, setShowModal] = useState(false);

  const valueType = getValueType(value);
  const isComplex = valueType === "object" || valueType === "array";
  const isEditable = !isComplex && onEdit;

  const handleStartEdit = () => {
    if (isComplex) {
      setShowModal(true);
    } else {
      setEditValue(formatValueForEdit(value));
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    try {
      const parsedValue = parseEditValue(editValue, valueType);
      onEdit?.(keyName, parsedValue);
      setIsEditing(false);
    } catch (error) {
      alert(`Invalid value for type ${valueType}`);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue("");
  };

  return (
    <>
      <tr className="border-b border-gray-200 hover:bg-gray-50">
        {/* Key */}
        <td className="px-3 py-2 font-mono text-xs text-gray-700 font-medium">
          {keyName}
        </td>

        {/* Type */}
        <td className="px-3 py-2">
          <span
            className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getTypeBadgeColor(
              valueType,
            )}`}
          >
            {valueType}
          </span>
        </td>

        {/* Value */}
        <td className="px-3 py-2 font-mono text-xs max-w-md">
          {isEditing ? (
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="w-full px-2 py-1 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave();
                if (e.key === "Escape") handleCancel();
              }}
            />
          ) : (
            <span className={getValueColor(value)}>
              {formatValueForDisplay(value)}
            </span>
          )}
        </td>

        {/* Actions */}
        {onEdit && (
          <td className="px-3 py-2">
            {isEditing ? (
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={handleSave}
                  className="p-1 hover:bg-green-100 rounded text-green-600"
                  title="Save"
                >
                  <Check className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="p-1 hover:bg-red-100 rounded text-red-600"
                  title="Cancel"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleStartEdit}
                className="p-1 hover:bg-blue-100 rounded text-blue-600"
                title={isComplex ? "Edit in modal" : "Edit inline"}
              >
                {isComplex ? (
                  <ExternalLink className="h-4 w-4" />
                ) : (
                  <Edit2 className="h-4 w-4" />
                )}
              </button>
            )}
          </td>
        )}
      </tr>

      {/* Modal for complex editing */}
      {showModal && (
        <ComplexEditModal
          keyName={keyName}
          value={value}
          onSave={(newValue) => {
            onEdit?.(keyName, newValue);
            setShowModal(false);
          }}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}

interface ComplexEditModalProps {
  keyName: string;
  value: unknown;
  onSave: (value: unknown) => void;
  onClose: () => void;
}

function ComplexEditModal({
  keyName,
  value,
  onSave,
  onClose,
}: ComplexEditModalProps) {
  const [editText, setEditText] = useState(JSON.stringify(value, null, 2));
  const [error, setError] = useState<string | null>(null);

  const handleSave = () => {
    try {
      const parsed = JSON.parse(editText);
      onSave(parsed);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid JSON");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Edit: {keyName}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden px-6 py-4">
          <textarea
            value={editText}
            onChange={(e) => {
              setEditText(e.target.value);
              setError(null);
            }}
            className="w-full h-full font-mono text-sm border border-gray-300 rounded p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            placeholder="Enter JSON..."
          />
          {error && <p className="mt-2 text-sm text-red-600">Error: {error}</p>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper functions

function getValueType(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (Array.isArray(value)) return "array";
  if (typeof value === "object") return "object";
  return typeof value;
}

function getTypeBadgeColor(type: string): string {
  switch (type) {
    case "string":
      return "bg-green-100 text-green-700";
    case "number":
      return "bg-blue-100 text-blue-700";
    case "boolean":
      return "bg-purple-100 text-purple-700";
    case "object":
      return "bg-orange-100 text-orange-700";
    case "array":
      return "bg-pink-100 text-pink-700";
    case "null":
    case "undefined":
      return "bg-gray-100 text-gray-600";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

function getValueColor(value: unknown): string {
  if (value === null || value === undefined) return "text-gray-400";
  if (typeof value === "string") return "text-green-600";
  if (typeof value === "boolean") return "text-purple-600";
  if (typeof value === "number") return "text-blue-600";
  return "text-gray-700";
}

function formatValueForDisplay(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "string") return `"${value}"`;
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    const preview = value.slice(0, 3).map(formatValueForDisplay).join(", ");
    return `[${preview}${value.length > 3 ? ", ..." : ""}] (${value.length} items)`;
  }
  if (typeof value === "object") {
    const keys = Object.keys(value);
    return `{ ${keys.slice(0, 3).join(", ")}${keys.length > 3 ? ", ..." : ""} } (${keys.length} fields)`;
  }
  return String(value);
}

function formatValueForEdit(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "string") return value;
  return String(value);
}

function parseEditValue(text: string, expectedType: string): unknown {
  const trimmed = text.trim();

  if (trimmed === "null") return null;
  if (trimmed === "undefined") return undefined;

  switch (expectedType) {
    case "string":
      return text;
    case "number": {
      const num = Number(text);
      if (Number.isNaN(num)) throw new Error("Invalid number");
      return num;
    }
    case "boolean":
      if (trimmed === "true") return true;
      if (trimmed === "false") return false;
      throw new Error("Invalid boolean (must be 'true' or 'false')");
    default:
      throw new Error(`Cannot parse type: ${expectedType}`);
  }
}
